import { Popover, Transition } from "@headlessui/react";
import clsx from "clsx";
import NavWrapper from "./NavWrapper";
import { Fragment, useState, useRef } from "react";
import { FocusTrap } from "focus-trap-react";
import { Arrow, Carrot } from "../icons";
import SearchTakeover from "../search/SearchTakeover";
import LogoWhiteOnBlack from "../icons/svgs/LogoWhiteOnBlack";
import NavOrExternalLink from "../shared/NavOrExternalLink";
import { PreventBodyScroll } from "../utils/PreventBodyScroll";
import { NavLinkContentful } from "./NavContentful";

type MobileNavProps = {
  links: NavLinkContentful[];
};
const heading16 = "text-16 leading-24 font-medium text-white uppercase";
const section = "border-b border-solid border-white py-6";

export default function MobileNavContentful({ links }: MobileNavProps) {
  const [activeSubmenu, setActiveSubmenu] = useState<NavLinkContentful | null>(
    null,
  );
  const mainMenuButtonRefs = useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});

  // Handle going back to main menu with proper focus
  const handleBackToMainMenu = () => {
    const currentSubmenuTitle = activeSubmenu?.title;
    setActiveSubmenu(null);

    // Focus the button that opened this submenu
    if (currentSubmenuTitle) {
      const buttonToFocus = mainMenuButtonRefs.current[currentSubmenuTitle];
      buttonToFocus?.focus();
    }
  };

  return (
    <Popover as={Fragment}>
      {({ open, close }) => {
        // Reset submenu when main menu closes
        if (!open && activeSubmenu) {
          setActiveSubmenu(null);
        }

        return (
          <NavWrapper
            id="top-nav-mobile"
            open={open}
            className="lg:hidden"
            isContentfulNav
          >
            <PreventBodyScroll enabled={open} />
            <div className="flex w-full items-center justify-between">
              <NavOrExternalLink to="/" title="home" tabIndex={open ? -1 : 0}>
                <LogoWhiteOnBlack id="mobile_logo_black" />
              </NavOrExternalLink>
              <div className="flex w-max justify-between">
                {!open && <SearchTakeover />}
                <Popover.Button
                  id="open_close_mobile"
                  className="ml-6"
                  tabIndex={0}
                >
                  {open ? "CLOSE" : "MENU"}
                </Popover.Button>
              </div>
            </div>
            <Transition
              show={open}
              enter="transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Popover.Panel
                className={clsx(
                  "absolute top-mobile-nav left-0 z-10 flex h-[calc(100vh-theme(space[mobile-nav]))] min-h-[550px] w-screen flex-col bg-black",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    if (activeSubmenu) {
                      // If in submenu, go back to main menu
                      handleBackToMainMenu();
                    } else {
                      // If in main menu, close the whole menu
                      close();
                    }
                  }
                }}
              >
                {/* Main Menu */}
                <FocusTrap
                  active={open && !activeSubmenu}
                  focusTrapOptions={{
                    allowOutsideClick: true,
                    returnFocusOnDeactivate: false,
                    escapeDeactivates: false,
                    initialFocus: false,
                  }}
                >
                  <div
                    className="absolute inset-0 overflow-y-auto"
                    aria-hidden={!!activeSubmenu}
                  >
                    <ul
                      id="top-nav-popover-menu-mobile"
                      className="mt-[33px] ml-4 mr-4 flex flex-col"
                      role="menu"
                      aria-label="Main navigation menu"
                    >
                      {links.map((link) => (
                        <Fragment key={link.title}>
                          {link.headersCollection &&
                          link.headersCollection.length > 0 ? (
                            <button
                              ref={(el) => {
                                if (link.title) {
                                  mainMenuButtonRefs.current[link.title] = el;
                                }
                              }}
                              className={`flex w-full items-center justify-between ${section} text-left text-white `}
                              onClick={() => setActiveSubmenu(link)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setActiveSubmenu(link);
                                }
                              }}
                              role="menuitem"
                              aria-label={`Open ${link.title} submenu`}
                              aria-expanded={
                                activeSubmenu?.title === link.title
                              }
                              aria-haspopup="menu"
                              aria-hidden={!open || !!activeSubmenu}
                              title={`Open ${link.title} submenu`}
                              tabIndex={open && !activeSubmenu ? 0 : -1}
                            >
                              <span className={heading16}>{link.title}</span>
                              <Carrot
                                className="-rotate-90"
                                aria-hidden="true"
                              />
                            </button>
                          ) : (
                            <NavOrExternalLink
                              to={""}
                              key={link.title}
                              id={`top-nav-${link.title}`}
                              className={clsx(section, heading16)}
                              onClick={() => close()}
                              prefetch="intent"
                              role="menuitem"
                              aria-label={`Navigate to ${link.title}`}
                              aria-hidden={!open || !!activeSubmenu}
                              tabIndex={open && !activeSubmenu ? 0 : -1}
                            >
                              {link.title}
                            </NavOrExternalLink>
                          )}
                        </Fragment>
                      ))}
                    </ul>
                  </div>
                </FocusTrap>

                {/* Submenu - slides in from right */}
                <FocusTrap
                  active={!!activeSubmenu}
                  focusTrapOptions={{
                    allowOutsideClick: true,
                    returnFocusOnDeactivate: false,
                    escapeDeactivates: false,
                  }}
                >
                  <div
                    className={clsx(
                      "ease-in-out absolute inset-0 z-10 h-[calc(100vh-2*theme(space[mobile-nav]))] overflow-y-auto bg-black transition-transform duration-300",
                      activeSubmenu ? "translate-x-0" : "translate-x-full",
                    )}
                    role="menu"
                    aria-label={
                      activeSubmenu
                        ? `${activeSubmenu.title} submenu`
                        : "Submenu"
                    }
                    aria-hidden={!activeSubmenu}
                  >
                    {activeSubmenu && (
                      <div className="mt-[33px] ml-4 mr-4 flex flex-col">
                        {/* Back Button */}
                        <button
                          className="flex w-full items-center pt-6 text-left text-white"
                          onClick={handleBackToMainMenu}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleBackToMainMenu();
                            }
                          }}
                          aria-label="Back to main navigation"
                          aria-hidden={!activeSubmenu}
                          title="Back to main navigation"
                          tabIndex={activeSubmenu ? 0 : -1}
                          autoFocus={!!activeSubmenu}
                        >
                          <Carrot className="mr-3 rotate-90" />
                          <span className="text-[16px]">BACK</span>
                        </button>
                        {/* Description */}
                        <div className="border-b border-solid border-white pt-9 pb-6">
                          {activeSubmenu.descriptionHeader && (
                            <span className="ppe text-[20px]">
                              {activeSubmenu.descriptionHeader}
                            </span>
                          )}
                          {activeSubmenu.descriptionText && (
                            <div className="pt-4">
                              {activeSubmenu.descriptionText}
                            </div>
                          )}
                        </div>

                        {/* Submenu Items */}
                        {activeSubmenu.headersCollection.map((header) => (
                          <div key={header.label} className={clsx(section)}>
                            {header.url ? (
                              <NavOrExternalLink
                                to={`${header.url}`}
                                className={clsx(
                                  "cta relative flex w-full flex-wrap content-start items-center",
                                  heading16,
                                )}
                                onClick={() => close()}
                                prefetch="intent"
                                aria-hidden={!activeSubmenu}
                                tabIndex={activeSubmenu ? 0 : -1}
                              >
                                <span className="mr-4">{header.label}</span>
                                <span className="flex-center -translate-y-[2px] rounded-full bg-white p-0 text-black">
                                  <Arrow />
                                </span>
                              </NavOrExternalLink>
                            ) : (
                              <div className={clsx("aeonik-base", heading16)}>
                                {header.label}
                              </div>
                            )}
                            {/* Submenu Items */}
                            {header.linkCollection &&
                              header.linkCollection.length > 0 && (
                                <div className="mt-6 flex flex-col gap-4">
                                  {header.linkCollection.map((sublink) => (
                                    <NavOrExternalLink
                                      key={sublink.label}
                                      to={sublink.url}
                                      className="aeonik block text-16 leading-24"
                                      onClick={() => close()}
                                      prefetch="intent"
                                      aria-hidden={!activeSubmenu}
                                      tabIndex={activeSubmenu ? 0 : -1}
                                    >
                                      {sublink.label}
                                    </NavOrExternalLink>
                                  ))}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </FocusTrap>
              </Popover.Panel>
            </Transition>
          </NavWrapper>
        );
      }}
    </Popover>
  );
}
